import { Typography, Box, styled, Stack } from "@mui/material";
import { basicColours, mainColours } from "@qubit/autoparts";

import { useAppSelector } from "~/app/store";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { PickingStateV2TotePick } from "~/types/api";

type Props = {
  allPicksForTote: PickingStateV2TotePick[];
  hasExternalToteId: boolean;
};

const Container = styled(Stack)(
  ({
    ap_fusionPortScreenEnabled,
    hasExternalToteId
  }: {
    ap_fusionPortScreenEnabled: boolean;
    hasExternalToteId: boolean;
  }) => ({
    alignItems: "center",
    justifyContent: "center",
    alignSelf: ap_fusionPortScreenEnabled
      ? "unset"
      : hasExternalToteId
        ? "anchor-center"
        : "flex-end",
    height: ap_fusionPortScreenEnabled ? 240 : 252,
    minWidth: "max-content",
    marginLeft: 10
  })
);

const ProgressBarTrack = styled(Box)(
  ({
    ap_fusionPortScreenEnabled
  }: {
    ap_fusionPortScreenEnabled: boolean;
  }) => ({
    display: "flex",
    position: "relative",
    width: 20,
    height: ap_fusionPortScreenEnabled ? 120 : 252,
    borderRadius: 10,
    backgroundColor: basicColours.mono[200]
  })
);

const ProgressBarFill = styled("span")<{ percent: number }>(({ percent }) => ({
  position: "absolute",
  bottom: 0,
  width: 20,
  height: `${percent}%`,
  borderRadius: 10,
  backgroundColor: mainColours.infographic[1],
  transition: "height 0.5s ease-in-out"
}));

const ToteProgressBarV2 = ({ allPicksForTote, hasExternalToteId }: Props) => {
  const { ap_fusionPortScreenEnabled } = useAppSelector(selectServerConfig);

  const totalPicks = allPicksForTote.reduce(
    (acc, curr) =>
      curr.canceledReason || curr.canceledTimestamp ? acc : acc + curr.quantity,
    0
  );
  const completedPicks = allPicksForTote.reduce(
    (acc, curr) => (curr.completedTimestamp ? acc + curr.quantity : acc),
    0
  );
  const percentComplete =
    totalPicks === 0 ? 0 : (completedPicks / totalPicks) * 100;

  return (
    <Container
      ap_fusionPortScreenEnabled={ap_fusionPortScreenEnabled}
      hasExternalToteId={hasExternalToteId}
    >
      <ProgressBarTrack ap_fusionPortScreenEnabled={ap_fusionPortScreenEnabled}>
        <ProgressBarFill
          percent={percentComplete}
          aria-label="progress-bar-fill"
        />
      </ProgressBarTrack>

      <Typography
        variant="h6"
        color={basicColours.mono.black}
        fontWeight={500}
        sx={{ opacity: "60%" }}
      >
        {`${completedPicks} / ${totalPicks}`}
      </Typography>
    </Container>
  );
};

export default ToteProgressBarV2;
